import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { X, FileText, Download, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';

interface FormVersion {
  id: string;
  version_number: string;
  file_url: string;
  file_type: string;
  status: string;
  effective_date: string;
  expiration_date: string | null;
  applicable_states: string[];
  change_notes: string;
  regulatory_ref: string | null;
  uploaded_by: string;
  uploaded_at: string;
  approved_by: string | null;
  approved_at: string | null;
  approval_comments: string | null;
  kill_switch_activated: boolean;
  kill_switch_reason: string | null;
}

interface VersionHistoryModalProps {
  formCode: string;
  formName: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function VersionHistoryModal({
  formCode,
  formName,
  isOpen,
  onClose,
}: VersionHistoryModalProps) {
  const [versions, setVersions] = useState<FormVersion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && formCode) {
      loadVersions();
    }
  }, [isOpen, formCode]);

  const loadVersions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('form_versions')
        .select('*')
        .eq('form_code', formCode)
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      setVersions(data || []);
    } catch (error) {
      console.error('Error loading versions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (version: FormVersion) => {
    if (version.kill_switch_activated) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-red-100 text-red-800">
          <AlertTriangle className="w-3 h-3" />
          Disabled
        </span>
      );
    }

    switch (version.status) {
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3" />
            Approved
          </span>
        );
      case 'pending_approval':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-gray-100 text-gray-800">
            <XCircle className="w-3 h-3" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800">
            {version.status}
          </span>
        );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Version History</h2>
              <p className="text-sm text-gray-600 mt-1">
                {formCode} - {formName}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : versions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>No versions found for this form</p>
              </div>
            ) : (
              <div className="space-y-4">
                {versions.map((version, index) => (
                  <div
                    key={version.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    {/* Version Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-gray-400" />
                          <span className="font-semibold text-lg text-gray-900">
                            Version {version.version_number}
                          </span>
                        </div>
                        {index === 0 && version.status === 'approved' && !version.kill_switch_activated && (
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                            Current
                          </span>
                        )}
                        {getStatusBadge(version)}
                      </div>
                      <a
                        href={version.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </a>
                    </div>

                    {/* Version Details */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Effective Date:</span>
                        <span className="ml-2 font-medium">
                          {new Date(version.effective_date).toLocaleDateString()}
                        </span>
                      </div>
                      {version.expiration_date && (
                        <div>
                          <span className="text-gray-600">Expiration Date:</span>
                          <span className="ml-2 font-medium">
                            {new Date(version.expiration_date).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-600">Uploaded:</span>
                        <span className="ml-2 font-medium">
                          {new Date(version.uploaded_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">File Type:</span>
                        <span className="ml-2 font-medium uppercase">{version.file_type}</span>
                      </div>
                    </div>

                    {/* Applicable States */}
                    {version.applicable_states && version.applicable_states.length > 0 && (
                      <div className="mt-3">
                        <span className="text-sm text-gray-600">Applicable States:</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {version.applicable_states.map((state) => (
                            <span
                              key={state}
                              className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded"
                            >
                              {state}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Change Notes */}
                    {version.change_notes && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm font-medium text-gray-700 mb-1">Changes:</p>
                        <p className="text-sm text-gray-600">{version.change_notes}</p>
                      </div>
                    )}

                    {/* Regulatory Reference */}
                    {version.regulatory_ref && (
                      <div className="mt-2">
                        <span className="text-sm text-gray-600">Regulatory Reference:</span>
                        <span className="ml-2 text-sm font-medium text-gray-900">
                          {version.regulatory_ref}
                        </span>
                      </div>
                    )}

                    {/* Approval Info */}
                    {version.approved_at && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-sm text-gray-600">
                          {version.status === 'approved' ? 'Approved' : 'Reviewed'} on{' '}
                          {new Date(version.approved_at).toLocaleDateString()}
                        </p>
                        {version.approval_comments && (
                          <p className="text-sm text-gray-700 mt-1">
                            Note: {version.approval_comments}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Kill-Switch Warning */}
                    {version.kill_switch_activated && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm font-semibold text-red-900 mb-1">
                          ⚠️ Kill-Switch Activated
                        </p>
                        <p className="text-sm text-red-700">{version.kill_switch_reason}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
